<template>
  <div class="invalid-users-detail">
    <div class="invalid-users-detail__toolbar">
      <BaseTextInput
        v-model="searchQuery"
        type="search"
        class="invalid-users-detail__search"
        :label="$t('userUpload.SYNC_SUMMARY_SEARCH_LABEL')"
        :placeholder="$t('userUpload.SYNC_SUMMARY_SEARCH_PLACEHOLDER')"
      />
      <div class="invalid-users-detail__filter">
        <label
          class="invalid-users-detail__filter-label"
          for="invalid-users-detail-filter"
        >
          {{ $t('userUpload.SYNC_SUMMARY_ERROR_TYPE') }}
        </label>
        <BaseSelect id="invalid-users-detail-filter" v-model="errorFilter">
          <BaseOption
            v-for="option in errorFilterOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.display }}
          </BaseOption>
        </BaseSelect>
      </div>
    </div>

    <div class="invalid-users-detail__table-container">
      <BTableSimple sticky-header small class="invalid-users-detail__table">
        <colgroup>
          <col class="invalid-users-detail__col--id" />
          <col class="invalid-users-detail__col--name" />
          <col class="invalid-users-detail__col--name" />
          <col class="invalid-users-detail__col--email" />
          <col class="invalid-users-detail__col--phone" />
          <col class="invalid-users-detail__col--errors" />
        </colgroup>
        <BThead>
          <BTr>
            <BTh>{{ $t('EXTERNAL_USER_ID') }}</BTh>
            <BTh>{{ $t('FIRSTNAME') }}</BTh>
            <BTh>{{ $t('LASTNAME') }}</BTh>
            <BTh>{{ $t('EMAIL') }}</BTh>
            <BTh>{{ $t('PHONE') }}</BTh>
            <BTh class="invalid-users-detail__errors-header">
              {{ $t('userUpload.SYNC_SUMMARY_INVALID_USERS_ERRORS') }}
            </BTh>
          </BTr>
        </BThead>
        <BTbody>
          <BTr v-for="(row, index) in filteredUsers" :key="index">
            <BTd>{{ row.user.external_user_id }}</BTd>
            <BTd>{{ row.user.first_name }}</BTd>
            <BTd>{{ row.user.last_name }}</BTd>
            <BTd>{{ row.user.email }}</BTd>
            <BTd>{{ row.user.phone_number }}</BTd>
            <BTd class="invalid-users-detail__errors-cell">
              <span
                v-if="row.errors.length === 1"
                class="invalid-users-detail__error-text"
              >
                {{ formatError(row.errors[0]) }}
              </span>
              <BaseTooltip v-else position="left">
                <template #trigger>
                  <span class="invalid-users-detail__error-count">
                    {{ row.errors.length }}
                  </span>
                </template>
                <template #content>
                  <ul class="invalid-users-detail__tooltip-list">
                    <li v-for="(error, i) in row.errors" :key="i">
                      {{ formatError(error) }}
                    </li>
                  </ul>
                </template>
              </BaseTooltip>
            </BTd>
          </BTr>
        </BTbody>
      </BTableSimple>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseSelect, {
  BaseOption,
} from '@/components/generics/base/BaseSelect.vue'
import BaseTextInput from '@/components/generics/base/BaseTextInput.vue'
import BaseTooltip from '@/components/generics/base/BaseTooltip.vue'
import { useI18n } from '@/hooks/useI18N'
import type { InvalidUser } from '@/queries/userUpload/userUploadTypes'
import { BTableSimple, BTbody, BTd, BTh, BThead, BTr } from 'bootstrap-vue'
import { startCase } from 'lodash-es'
import { computed, ref } from 'vue'
import { getClassifiedErrors, type ClassifiedError } from './invalidUserErrors'

type EnrichedUser = {
  user: InvalidUser
  errors: ClassifiedError[]
}

const props = defineProps<{
  invalidUsers: InvalidUser[]
}>()

const i18n = useI18n()

const ALL_ERRORS = 'all'

const errorFilter = ref<string>(ALL_ERRORS)
const searchQuery = ref('')

const matchesSearch = (user: InvalidUser, query: string): boolean => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return Object.values(user).some(
    (value) => typeof value === 'string' && value.toLowerCase().includes(q)
  )
}

const getCategoryLabel = (category: string): string => {
  const key = `userUpload.INVALID_USER_FIELDS.${category}`
  return i18n.t(key) || startCase(category)
}

/** Render an error for display; base errors show the message alone. */
const formatError = ({ field, message }: ClassifiedError): string =>
  field === 'base' ? message : `${getCategoryLabel(field)}: ${message}`

const enrichedUsers = computed<EnrichedUser[]>(() =>
  props.invalidUsers.map((user) => ({
    user,
    errors: getClassifiedErrors(user),
  }))
)

const errorFilterOptions = computed(() => {
  const keys = new Set<string>()
  for (const row of enrichedUsers.value) {
    for (const error of row.errors) keys.add(error.key)
  }
  return [
    { value: ALL_ERRORS, display: i18n.t('userUpload.SYNC_SUMMARY_ALL') },
    ...Array.from(keys, (key) => ({
      value: key,
      display: getCategoryLabel(key),
    })),
  ]
})

const filteredUsers = computed(() =>
  enrichedUsers.value.filter((row) => {
    const matchesFilter =
      errorFilter.value === ALL_ERRORS ||
      row.errors.some((error) => error.key === errorFilter.value)
    return matchesFilter && matchesSearch(row.user, searchQuery.value)
  })
)
</script>

<style lang="scss" scoped>
.invalid-users-detail {
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;

  &__toolbar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: $spacing-s;
  }

  &__search {
    flex: 1;
    max-width: 320px;
  }

  &__filter {
    display: flex;
    flex-direction: column;
    width: 220px;
  }

  &__filter-label {
    @include formLabelStyle;
  }

  &__table-container {
    overflow-x: auto;
    overflow-y: auto;
    height: 400px;
  }

  &__table {
    font-size: $font-size-s;
    table-layout: fixed;
  }

  &__col {
    &--id {
      width: 130px;
    }

    &--name {
      width: 130px;
    }

    &--email {
      width: 220px;
    }

    &--phone {
      width: 140px;
    }

    &--errors {
      width: 160px;
    }
  }

  &__errors-header {
    text-align: right;
  }

  &__errors-cell {
    text-align: right;
    vertical-align: top;
  }

  &__error-text {
    color: $color-text-light;
  }

  &__error-count {
    color: $color-blue-45;
    font-weight: 600;
    cursor: help;
  }

  &__tooltip-list {
    margin: 0;
    padding: 0;
    text-align: left;
    li {
      list-style: disc inside;
    }
  }
}
</style>
